import { useEffect, useState } from "react";
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip } from "chart.js";
import { Line } from "react-chartjs-2";
import { Row, Col, Spinner, Dropdown } from "react-bootstrap";
import api from "../../../../common/api";
import { RiLineChartLine } from "react-icons/ri";
import "./AnalyticsChart.css";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, {
  id: "verticalLine",
  afterDraw({ ctx, tooltip, chartArea }) {
    if (!tooltip?._active?.length) return;
    const x = tooltip._active[0].element.x;
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(x, chartArea.top);
    ctx.lineTo(x, chartArea.bottom);
    ctx.setLineDash([1, 4]);
    ctx.strokeStyle = "#6366f1";
    ctx.lineWidth = 1.5;
    ctx.stroke();
    ctx.restore();
  },
});

const rgba = (hex, a) => `rgba(${parseInt(hex.slice(1,3),16)},${parseInt(hex.slice(3,5),16)},${parseInt(hex.slice(5,7),16)},${a})`;
const formatK = (v) => v >= 1e6 ? (v/1e6).toFixed(1)+"M" : v >= 1000 ? (v/1000).toFixed(1)+"k" : v;

export default function PlansetGrowthChart() {
  const [mode, setMode] = useState("months");
  const [apiData, setApiData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/plansets/monthly-chart")
      .then((r) => setApiData(r.data.monthly_counts || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const isMonths = mode === "months";
  const source = isMonths ? apiData : apiData.find((i) => i.month === mode)?.daily_counts || [];
  const labels = source.map((i) => isMonths ? i.month : i.date);
  const data = source.map((i) => Number(isMonths ? i.total_count : i.count));
  const hasData = data.some(Boolean);

  const chartData = {
    labels,
    datasets: [{
      label: isMonths ? "Total Plansets" : "Daily Plansets",
      data,
      yAxisID: "y",
      borderColor: "#6366f1",
      backgroundColor: ({ chart }) => {
        if (!chart.chartArea) return rgba("#6366f1", 0.15);
        const g = chart.ctx.createLinearGradient(0, chart.chartArea.top, 0, chart.chartArea.bottom);
        g.addColorStop(0, rgba("#6366f1", 0.26));
        g.addColorStop(0.5, rgba("#6366f1", 0.08));
        g.addColorStop(1, rgba("#6366f1", 0));
        return g;
      },
      borderWidth: 2, pointRadius: 5, pointBackgroundColor: "#6366f1",
      pointBorderColor: "#fff", pointBorderWidth: 1, pointHoverRadius: 2,
      tension: 0.3, fill: true,
    }],
  };

  const options = {
    responsive: true, maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: ([item]) => isMonths ? item.label : `${item.label} ${mode}`,
          label: (item) => ` ${item.dataset.label}: ${Number(item.raw).toLocaleString()}`,
        }}},
    scales: {
      x: { grid: { color: "#f3f4f6" }, ticks: { color: "#9ca3af", font: { size: 12 } } },
      y: { beginAtZero: true, ticks: { callback: formatK, color: "#6366f1", font: { size: 11 } }, grid: { color: "#f3f4f6" } },
    },
  };

  const selectOptions = [{ value: "months", label: "All Months" }, ...apiData.map((i) => ({ value: i.month, label: i.month }))];

  return (
    <Row className="flex-column">
      <Col>
        <Row className="align-items-start mb-3">
          <Col>
            <p className="planset-title fw-bold mb-0">Planset Distribution</p>
            <p className="planset-subtitle mb-0">{isMonths ? "Monthly Plansets" : `Daily orders for ${mode}`}</p>
          </Col>
          <Col xs="auto">
            <Dropdown className="client-dropdown">
             <Dropdown.Toggle variant="white">{selectOptions.find((o) => o.value === mode)?.label}</Dropdown.Toggle>
             <Dropdown.Menu className="client-dropdown-menu">{selectOptions.map((o) => (<Dropdown.Item key={o.value} onClick={() => setMode(o.value)}>{o.label}</Dropdown.Item>))}</Dropdown.Menu>
           </Dropdown>
          </Col>
        </Row>
      </Col>
      <Col>
        <Row className="planset-chart-area m-0">
          <Col className="p-0 h-100">
            {loading ? <Row className="h-100 align-items-center justify-content-center m-0"><Col xs="auto"><Spinner animation="border" size="sm" /></Col></Row>
             : !hasData ? 
              <Row className="planset-no-data h-100 flex-column align-items-center justify-content-center m-0">
                <Col xs="auto"><RiLineChartLine size={30} /></Col>
                <Col xs="auto"><p className="planset-no-data_title mb-0">No Data Available</p></Col>
                <Col xs="auto"><p className="planset-no-data_subtitle mb-0">No records found for this period</p></Col>
              </Row> : <Line data={chartData} options={options} />
            }
          </Col>
        </Row>
      </Col>
    </Row>
  );
}